import {
    type Employee,
    type ExtendedReferencedVendor,
    type SystemRightEnum,
    type UserApprover,
    type UserRule,
    type UserSettingsInterface,
    type Vendor,
    type VendorRule,
} from "@finway-group/shared/lib/models"
import { type EmployeeAbsence } from "@finway-group/shared/lib/models/user/employeeAbsence.model"
import type UserCompanyProfileInterface from "@finway-group/shared/lib/models/user/userCompanyProfileInterface"
import { removeQueryString } from "@finway-group/shared/lib/utils"
import { isNotSet } from "@finway-group/shared/lib/utils/common"

export interface UserCreditor {
    id: string
    email: string
    workPhone?: string
    position: string
    team: string
    systemRights: Array<SystemRightEnum>
    approvals?: Array<UserApprover>
    img?: string
    settings: UserSettingsInterface
    passwordExpired?: boolean
    tfaValid?: boolean
    weavrData: any
    companyProfiles?: Array<UserCompanyProfileInterface>
    dateDeleted?: Date
    deleted?: boolean
    tenantId?: string
    rule?: Array<UserRule>
    firstName: string
    lastName: string
    location: string
    superior?: string
    bic?: string
    iban?: string
    activeCompanyProfile: UserCompanyProfileInterface
    absence?: EmployeeAbsence
    expensesSoFar: number
    name: string
    creditorNumber?: string
}

export const mapEmployeeToUserCreditor = (employee: Employee): UserCreditor => ({
    ...employee,
    name: employee.getFullName(),
    creditorNumber: employee?.activeCompanyProfile?.creditorNumber ? String(employee.activeCompanyProfile.creditorNumber) : undefined,
})

export interface DuplicateVendorInformationInterface {
    vendor: Vendor
    matchedAttributes: Array<keyof Vendor>
}

export const getAllMatchedAttributes = (duplicateVendors: Array<DuplicateVendorInformationInterface>) => [
    ...new Set(duplicateVendors.flatMap(({ matchedAttributes }) => matchedAttributes)),
]

/* Vendor rule object could have only _id defined therefore at least two values need to be defined */
export const isVendorRuleEmpty = (rule: VendorRule | undefined) => Object.values(rule || {}).filter(Boolean).length < 2

export const isVendorCostCenterEmpty = (rule: VendorRule | undefined) => isNotSet(rule?.costCenter)

export const getVendorLocationLabel = (expenseVendor: ExtendedReferencedVendor) => {
    let locationLabel = ""
    if (!expenseVendor?.city && !expenseVendor?.zip && !expenseVendor?.country) {
        locationLabel = "-"
    } else if (expenseVendor?.city && expenseVendor?.country) {
        locationLabel = `${expenseVendor.city} ${expenseVendor.zip ? `(${expenseVendor.zip}` : ""}, ${expenseVendor.country})`
    } else if (expenseVendor.country) {
        locationLabel = expenseVendor.country
    }

    return locationLabel
}

export interface VendorContractDocument {
    filePath: string
    documentNumber: number
}

/** Stored oldest-first (append order). Returned newest-first. `documentNumber` stays the 1-based upload position. */
export const getVendorContractDocuments = (contracts: Array<string>): Array<VendorContractDocument> =>
    contracts.map((filePath, index) => ({ filePath, documentNumber: index + 1 })).reverse()

export const getVendorContractTabKey = (filePath: string) => removeQueryString(filePath)

export const appendVendorContract = ({ contracts, filePath }: { contracts: Array<string>; filePath: string }) => [...contracts, filePath]

export const removeVendorContract = ({ contracts, filePath }: { contracts: Array<string>; filePath: string }) => {
    const targetKey = getVendorContractTabKey(filePath)
    return contracts.filter((contract) => getVendorContractTabKey(contract) !== targetKey)
}

export const resolveActiveVendorContractKey = ({
    activeContractKey,
    isAddingContract,
    contractDocuments,
    newContractKey,
}: {
    activeContractKey: string | undefined
    isAddingContract: boolean
    contractDocuments: Array<VendorContractDocument>
    newContractKey: string
}): string | undefined => {
    const newestContractKey = contractDocuments[0] ? getVendorContractTabKey(contractDocuments[0].filePath) : undefined
    if (activeContractKey === newContractKey) {
        return isAddingContract ? newContractKey : newestContractKey
    }
    const activeContractStillExists = contractDocuments.some((contractDocument) => getVendorContractTabKey(contractDocument.filePath) === activeContractKey)
    if (activeContractKey && activeContractStillExists) {
        return activeContractKey
    }
    if (isAddingContract) {
        return newContractKey
    }
    return newestContractKey
}
